/**
 * Current-contract status shown in the employee list, per the HR rule tables.
 *
 * The activation nuance (`current_activated` / `current_not_activated`) only
 * exists when the installation-level "Contract activation" option is enabled
 * (`global.contratclosing='O'`); otherwise a current contract collapses to the
 * single `current` state and the activation flag is ignored.
 */
export type ContractStatus = 'current_activated' | 'current_not_activated' | 'current' | 'none';

/** Normalized inputs — mapping from raw DICO values (`'O'`/`'N'`, tinyint) happens
 *  at the API/mapper boundary so this resolver stays representation-agnostic. */
export interface ContractStatusInput {
  /** Installation "Contract activation" option (`global.contratclosing='O'`), uniform per ONG. */
  activationOptionEnabled: boolean;
  /** `contratencours='O'` — an empty/null contract flag is `false` (no current contract). */
  hasCurrentContract: boolean;
  /** `statutactive='O'` — only meaningful when `activationOptionEnabled` is true. */
  isActivated: boolean;
}

/**
 * Resolve the display status from the (normalized) contract flags.
 *
 * Option active   → `none` | `current_activated` | `current_not_activated`
 * Option inactive → `none` | `current` (activation ignored)
 */
export function resolveContractStatus({
  activationOptionEnabled,
  hasCurrentContract,
  isActivated,
}: ContractStatusInput): ContractStatus {
  if (!hasCurrentContract) return 'none';
  if (!activationOptionEnabled) return 'current';
  return isActivated ? 'current_activated' : 'current_not_activated';
}
